import React, { useState, useEffect, useRef } from 'react'
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  IconButton,
  Box,
  Paper,
  Chip,
  Typography
} from '@material-ui/core'
import { Edit, Delete, Add, PersonOutline } from '@material-ui/icons'
import { toast } from 'react-toastify'

import toastError from '../../errors/toastError'
import api from '../../services/api'
import { ButtonSecondary } from '../ButtonSecondary'
import { useStyles } from './style'
import QuickTable from '../QuickTable'

const ReminderMessagesModal = ({ open, onClose }) => {
  const classes = useStyles()

  const messageInputRef = useRef(null)
  const [messages, setMessages] = useState([])
  const [editingMessage, setEditingMessage] = useState(null)
  const [loading, setLoading] = useState(false)
  const [messageText, setMessageText] = useState('')
  const [customFieldNames, setCustomFieldNames] = useState([])

  const baseAvailableVariables = [
    { label: '{{name}}', title: 'Nome completo do contato', icon: true },
    { label: '{{firstname}}', title: 'Primeiro nome do contato', icon: true },
    { label: '{{email}}', title: 'Email do contato' },
    { label: '{{greeting}}', title: 'Saudação (Olá, Bom dia, etc)' },
    { label: '{{time}}', title: 'Hora atual' },
    { label: '{{site}}', title: 'Site do contato' }
  ]

  const customFieldVariables = customFieldNames.map(name => ({
    label: `{{${name}}}`,
    title: `Campo customizado: ${name}`
  }))

  const availableVariables = [
    ...baseAvailableVariables,
    ...customFieldVariables
  ].filter(
    (variable, index, array) =>
      array.findIndex(item => item.label === variable.label) === index
  )

  const columns = [
    {
      field: 'message',
      headerName: 'Mensagem',
      align: 'left',
      // Estilização customizada para manter o comportamento de quebra de linha
      style: {
        whiteSpace: 'pre-wrap',
        wordBreak: 'break-word',
        maxHeight: '100px',
        overflow: 'auto'
      }
    }
  ]

  useEffect(() => {
    if (open) {
      fetchMessages()
      fetchCustomFieldNames()
    }
  }, [open])

  const fetchMessages = async () => {
    setLoading(true)
    try {
      const { data } = await api.get('/reminder-messages')
      setMessages(data)
    } catch (err) {
      toastError(err)
    } finally {
      setLoading(false)
    }
  }

  const fetchCustomFieldNames = async () => {
    try {
      const { data } = await api.get('/contacts/custom-fields/names')
      setCustomFieldNames(Array.isArray(data) ? data : [])
    } catch (err) {
      toastError(err)
      setCustomFieldNames([])
    }
  }

  const handleSave = async () => {
    if (!messageText.trim()) {
      toast.error('Digite uma mensagem')
      return
    }

    try {
      if (editingMessage) {
        await api.put(`/reminder-messages/${editingMessage.id}`, {
          message: messageText
        })
        toast.success('Mensagem atualizada com sucesso!')
      } else {
        await api.post('/reminder-messages', {
          message: messageText
        })
        toast.success('Mensagem criada com sucesso!')
      }

      setMessageText('')
      setEditingMessage(null)
      fetchMessages()
    } catch (err) {
      toastError(err)
    }
  }

  const handleEdit = message => {
    setEditingMessage(message)
    setMessageText(message.message)
  }

  const handleDelete = async id => {
    if (!window.confirm('Tem certeza que deseja excluir esta mensagem?')) {
      return
    }

    try {
      await api.delete(`/reminder-messages/${id}`)
      toast.success('Mensagem excluída com sucesso!')
      fetchMessages()
    } catch (err) {
      toastError(err)
    }
  }

  const handleCancel = () => {
    setMessageText('')
    setEditingMessage(null)
  }

  const insertVariable = variable => {
    const input = messageInputRef.current
    if (!input) return

    const textarea = input.querySelector('textarea')
    if (!textarea) return

    const start = textarea.selectionStart
    const end = textarea.selectionEnd
    const currentText = messageText || ''
    const newText =
      currentText.substring(0, start) + variable + currentText.substring(end)

    setMessageText(newText)

    setTimeout(() => {
      textarea.focus()
      textarea.setSelectionRange(
        start + variable.length,
        start + variable.length
      )
    }, 0)
  }

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle className={classes.dialogTitle}>
        Gerenciar Mensagens Pré-cadastradas
      </DialogTitle>
      <DialogContent dividers>
        <Box mb={3}>
          <Box mb={1}>
            <Typography variant="body2" color="textSecondary" gutterBottom>
              Variáveis disponíveis:
            </Typography>
            <Box display="flex" flexWrap="wrap" style={{ gap: 6 }}>
              {availableVariables.map(variable => (
                <Chip
                  key={variable.label}
                  icon={variable.icon ? <PersonOutline /> : undefined}
                  label={variable.label}
                  onClick={() => insertVariable(variable.label)}
                  color="primary"
                  variant="outlined"
                  size="small"
                  style={{ cursor: 'pointer' }}
                  title={variable.title}
                />
              ))}
            </Box>
          </Box>
          <TextField
            ref={messageInputRef}
            label="Mensagem"
            variant="outlined"
            fullWidth
            multiline
            minRows={3}
            value={messageText}
            onChange={e => setMessageText(e.target.value)}
            helperText="Use {{name}}, {{firstname}}, {{email}}, etc. para inserir variaveis na mensagem"
          />
          <Box mt={2} display="flex" gap={1}>
            <Button
              variant="contained"
              color="primary"
              startIcon={editingMessage ? <Edit /> : <Add />}
              onClick={handleSave}
            >
              {editingMessage ? 'Atualizar' : 'Adicionar'}
            </Button>
            {editingMessage && (
              <Button variant="outlined" onClick={handleCancel}>
                Cancelar
              </Button>
            )}
          </Box>
        </Box>

        <Paper variant="outlined">
          {/* <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Mensagem</TableCell>
                <TableCell align="right" style={{ width: 120 }}>
                  Ações
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {messages.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={2} align="center">
                    Nenhuma mensagem cadastrada
                  </TableCell>
                </TableRow>
              ) : (
                messages.map(message => (
                  <TableRow key={message.id}>
                    <TableCell>
                      <div
                        style={{
                          whiteSpace: 'pre-wrap',
                          wordBreak: 'break-word',
                          maxHeight: '100px',
                          overflow: 'auto'
                        }}
                      >
                        {message.message}
                      </div>
                    </TableCell>
                    <TableCell align="right">
                      <IconButton
                        size="small"
                        onClick={() => handleEdit(message)}
                      >
                        <Edit />
                      </IconButton>
                      <IconButton
                        size="small"
                        onClick={() => handleDelete(message.id)}
                      >
                        <Delete />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table> */}
          <QuickTable
            data={messages}
            columns={columns}
            loading={loading}
            actions={['edit', 'delete']}
            onEdit={handleEdit}
            onDelete={handleDelete}
            emptyMessage="Nenhuma mensagem cadastrada"
          />
        </Paper>
      </DialogContent>
      <DialogActions>
        <ButtonSecondary onClick={onClose} color="default" label="Fechar" />
      </DialogActions>
    </Dialog>
  )
}

export default ReminderMessagesModal
